import { MouseEvent, useEffect, useState, } from 'react';
import { SerializedError } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getClientAction,
  selectGetClientResult,
} from './clientSlice';
import { Client, ClientProvider } from '../../types';
import { ClientProviderModal, ClientProviderModalProps } from './ClientProviderModal';

type Props = {
  tenant_id: string;
  client_name: string;
  closeCallback: (error?: SerializedError) => void;
};

type ProviderModalProps = Pick<ClientProviderModalProps, 'show'|'init'>;

export function ClientView({ tenant_id, client_name, closeCallback }: Props) {
  const dispatch = useAppDispatch();
  const get_result = useAppSelector(selectGetClientResult);
  const [client, setClient] = useState<Client|undefined>();
  const [provider_modal_props, setProviderModalProps] = useState<ProviderModalProps>({ show: false });

  useEffect(() => {
    dispatch(getClientAction({ tenant_id, client_name }));
  }, [tenant_id, client_name, dispatch]);
  useEffect(() => {
    if (get_result) {
      if (getClientAction.fulfilled.match(get_result)) {
        setClient(get_result.payload);
      } else if (getClientAction.rejected.match(get_result)) {
        closeCallback(get_result.error);
      }
    }
  }, [get_result, closeCallback]);

  const handleShowProviderModal = (e: MouseEvent, provider: ClientProvider) => {
    e.preventDefault();
    setProviderModalProps({ show: true, init: provider });
  };
  const handleCloseProviderModal = () => {
    setProviderModalProps({ show: false });
  };
  return (
    <>
      <div className="mb-3 row">
        <label htmlFor="client_name" className="col-sm-3 col-form-label">Name</label>
        <div className="col-sm-6">
          <span id="client_name" className="form-control-plaintext">{client?.name}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="client_id" className="col-sm-3 col-form-label">ClientId</label>
        <div className="col-sm-6">
          <span id="client_id" className="form-control-plaintext">{client?.client_id}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="client_secret" className="col-sm-3 col-form-label">ClientSecret</label>
        <div className="col-sm-6">
          <span id="client_secret" className="form-control-plaintext">{client?.client_secret}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="description" className="col-sm-3 col-form-label">Description</label>
        <div className="col-sm-6">
          <span id="description" className="form-control-plaintext">{client?.description}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="full_role_id" className="col-sm-3 col-form-label">FullRoleId</label>
        <div className="col-sm-6">
          <span id="full_role_id" className="form-control-plaintext">{client?.full_role_id}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">AccessToken ExpiresIn(Second)</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <span className="form-control-plaintext">{client?.access_token.expires_in}</span>
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">AccessToken Include Scope</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <input type="checkbox" readOnly className="form-check-input"
              checked={client?.access_token.include_scope ?? false}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="refresh_token_is_enabled" className="col-sm-3 col-form-label">RefreshToken Enabled</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <input type="checkbox" readOnly className="form-check-input"
              checked={client?.refresh_token.is_enabled ?? false}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">RefreshToken ExpiresIn(Second)</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <span className="form-control-plaintext">{client?.refresh_token.expires_in}</span>
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Username Can Login</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <input type="checkbox" readOnly className="form-check-input"
              checked={client?.username.can_login ?? false}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Username Can Singup</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <input type="checkbox" readOnly className="form-check-input"
              checked={client?.username.can_signup ?? false}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Is Published</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <input type="checkbox" readOnly className="form-check-input"
              checked={client?.is_published ?? false}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="state" className="col-sm-3 col-form-label">Providers</label>
        <div className="mt-2 col-sm-6">
          {client?.providers.map(provider => (
            <a key={provider.name} className="me-1" href="/#" role="button" onClick={(e) => handleShowProviderModal(e, provider)}>
              <span className="badge bg-success px-2 py-1">{provider.name}</span>
            </a>
          ))}
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="state" className="col-sm-3 col-form-label">Authorized redirect URIs</label>
        <div className="mt-2 col-sm-9 row">
          {client?.authorized_redirect_uris.map((authorized_redirect_uri, index) => (
            <div key={index}>
              {authorized_redirect_uri}
            </div>
          ))}
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="client_state" className="col-sm-3 col-form-label">State</label>
        <div className="col-sm-6">
          <span id="client_state" className="form-control-plaintext">{client?.state}</span>
        </div>
      </div>
      <ClientProviderModal
        {...provider_modal_props}
        mode="view"
        onClose={handleCloseProviderModal}/>
    </>
  );
}
